import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LeadsTable } from '../components/LeadsTable';
import { EmptyState, ErrorPanel, RefreshingDot, SimulatedNotice } from '../components/PageState';
import { Badge, Button, Footnote, HeroBanner, Icon } from '../components/ui';
import { fetchListaDeLeads, fetchResumoDeLeads } from '../features/leads/api';
import { FiltrosDeLeads } from '../features/leads/components/FiltrosDeLeads';
import { OQueFazerAgoraLeads } from '../features/leads/components/OQueFazerAgoraLeads';
import { ResumoDoPeriodo } from '../features/leads/components/ResumoDoPeriodo';
import { baixarArquivoTexto, leadsParaCsv, nomeDoArquivoCsv } from '../features/leads/csv';
import { aplicarFiltros, CONSULTA_PADRAO, direcaoPadrao, escreverConsultaNaUrl, filtrosAtivos, lerConsultaDaUrl, ordenar, PAGE_SIZE } from '../features/leads/filtros';
import { gerarInsightsDaLista, type AcaoDoInsight } from '../features/leads/insights';
import type { ConsultaDeLeads, OrdenacaoDeLeads } from '../features/leads/types';
import { AGORA_SIMULADO, useApiResource } from '../lib/api';
import { useCompany } from '../lib/companyContext';
import { formatBRLFromCents, formatCount, formatDate } from '../lib/format';
import { describeSelection } from '../lib/period';
import { usePeriod } from '../lib/periodContext';

const AVISO_CSV_MS = 1_800;
const ATRASO_DA_BUSCA_MS = 300;

/**
 * Lista de leads: quem saiu do checkout (30 min sem pagar) ou teve o plano
 * vencido, onde cada um esta na regua e o que fazer a respeito (RNF15 / RNF16).
 *
 * Hierarquia, de cima para baixo:
 *  1. Faixa de identidade com a contagem do periodo.
 *  2. Resumo do periodo (sairam, recuperados, na regua agora, sem voltar),
 *     cada um comparado com o periodo anterior quando faz sentido.
 *  3. "O que fazer agora": ate quatro avisos lidos do resumo, com acao que
 *     recorta a propria lista.
 *  4. Filtros combinaveis (situacao, oferta, canal do ultimo contato, funil,
 *     alto valor, busca) com contagem por faceta, ordenacao e CSV.
 *  5. A tabela, carregada em fatias de 25 com "ver mais".
 *
 * Decisoes nao obvias:
 *  - A CONSULTA MORA NA URL (`?situacao=esgotado&alto=1&ordem=amount`):
 *    o "voltar" do detalhe cai no mesmo recorte, um link de insight de outra
 *    pagina abre a lista ja filtrada e a visao pode ser compartilhada. So a
 *    busca digitada espera 300 ms antes de ir para a URL, para nao refazer a
 *    requisicao a cada tecla.
 *  - "Ver mais" aumenta o `limit` da MESMA consulta (offset 0) em vez de
 *    somar paginas no cliente: uma requisicao so, mesma resposta que o
 *    servidor daria, e o dado anterior fica na tela enquanto a maior chega.
 *  - Filtro e ordem sao reaplicados em memoria sobre o que chegou. Com o
 *    mock e com o backend futuro e idempotente; com o backend de hoje (que
 *    ignora filtros) e o que mantem a tela honesta -- e as facetas vem
 *    marcadas como "nesta pagina".
 *  - Dois recursos independentes (lista e resumo), cada um com o proprio
 *    loading/erro: a rota nova de resumo nao pode derrubar a lista, que ja
 *    funciona no backend.
 *  - `company.id` entra nas dependencias dos fetchers: quando a
 *    administracao troca a empresa em foco, tudo recarrega em vez de
 *    misturar leads de duas empresas.
 *  - Toda leitura parte de um fato ja ocorrido. Nao existe "vai vencer" nem
 *    lembrete pre-vencimento nesta tela: sem o fato nao ha recuperacao e,
 *    portanto, nao ha comissao.
 */
export function Leads(): JSX.Element {
  const { company } = useCompany();
  const { range, selection } = usePeriod();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    document.title = `Leads — ${company.name} · Converte Fácil`;
  }, [company.name]);

  // Consulta = URL. `limite` e o unico pedaco que fica so em memoria.
  const consultaDaUrl = useMemo(() => lerConsultaDaUrl(location.search), [location.search]);
  const [limite, setLimite] = useState(PAGE_SIZE);
  useEffect(() => {
    setLimite(PAGE_SIZE);
  }, [location.search, range, company.id]);
  const consulta = useMemo<ConsultaDeLeads>(() => ({ ...consultaDaUrl, limit: limite, offset: 0 }), [consultaDaUrl, limite]);

  const atualizarConsulta = useCallback(
    (parcial: Partial<ConsultaDeLeads>): void => {
      const nova: ConsultaDeLeads = { ...lerConsultaDaUrl(location.search), ...parcial };
      navigate({ pathname: location.pathname, search: escreverConsultaNaUrl(location.search, nova) }, { replace: true });
    },
    [location.pathname, location.search, navigate],
  );

  // Busca digitada: estado local imediato, URL depois de uma pausa.
  const [busca, setBusca] = useState(consultaDaUrl.q);
  const buscaTimer = useRef<number | null>(null);
  useEffect(() => {
    setBusca(consultaDaUrl.q);
  }, [consultaDaUrl.q]);
  const digitarBusca = (texto: string): void => {
    setBusca(texto);
    if (buscaTimer.current !== null) {
      window.clearTimeout(buscaTimer.current);
    }
    buscaTimer.current = window.setTimeout(() => {
      atualizarConsulta({ q: texto });
    }, ATRASO_DA_BUSCA_MS);
  };
  useEffect(
    () => () => {
      if (buscaTimer.current !== null) {
        window.clearTimeout(buscaTimer.current);
      }
    },
    [],
  );

  const onFiltro = (parcial: Partial<ConsultaDeLeads>): void => {
    if (parcial.q !== undefined) {
      digitarBusca(parcial.q);
      return;
    }
    atualizarConsulta(parcial);
  };

  const limparFiltros = (): void => {
    setBusca('');
    atualizarConsulta({ ...CONSULTA_PADRAO });
  };

  const ordenarPor = (coluna: OrdenacaoDeLeads): void => {
    if (consulta.sort === coluna) {
      atualizarConsulta({ dir: consulta.dir === 'asc' ? 'desc' : 'asc' });
    } else {
      atualizarConsulta({ sort: coluna, dir: direcaoPadrao(coluna) });
    }
  };

  const executarAcao = (acao: AcaoDoInsight): void => {
    if (acao.kind === 'rota') {
      navigate({ pathname: acao.to, search: window.location.search });
      return;
    }
    setBusca(acao.consulta.q ?? '');
    atualizarConsulta({ ...CONSULTA_PADRAO, ...acao.consulta });
    document.getElementById('tabela-de-leads')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // `company.id` nas dependencias forca o refetch na troca de empresa (ver cabecalho).
  const buscar = useCallback((signal: AbortSignal) => fetchListaDeLeads(company.id, range, consulta, signal), [company.id, range, consulta]);
  const { state, reload } = useApiResource(buscar, 30_000);
  const buscarResumo = useCallback((signal: AbortSignal) => fetchResumoDeLeads(company.id, range, signal), [company.id, range]);
  const resumo = useApiResource(buscarResumo, 30_000);

  const agora = useMemo(() => (state.status === 'success' && state.simulated ? AGORA_SIMULADO : new Date()), [state]);
  const pagina = state.status === 'success' ? state.data : null;
  const resumoDados = resumo.state.status === 'success' ? resumo.state.data : null;

  const visiveis = useMemo(() => (pagina === null ? [] : ordenar(aplicarFiltros(pagina.items, consulta), consulta.sort, consulta.dir)), [pagina, consulta]);
  const insights = useMemo(() => (resumoDados === null ? [] : gerarInsightsDaLista(resumoDados, agora)), [resumoDados, agora]);
  const somaVisivelCents = useMemo(() => visiveis.reduce((acc, l) => acc + l.amountCents, 0), [visiveis]);

  const [csvGerado, setCsvGerado] = useState(false);
  useEffect(() => {
    if (!csvGerado) {
      return undefined;
    }
    const timer = window.setTimeout(() => setCsvGerado(false), AVISO_CSV_MS);
    return () => window.clearTimeout(timer);
  }, [csvGerado]);

  const exportarCsv = (): void => {
    if (visiveis.length === 0) {
      return;
    }
    baixarArquivoTexto(nomeDoArquivoCsv(company.slug, new Date()), leadsParaCsv(visiveis));
    setCsvGerado(true);
  };

  const ativos = filtrosAtivos(consulta);
  const simulado = (state.status === 'success' && state.simulated) || (resumo.state.status === 'success' && resumo.state.simulated);
  const totalNoPeriodo = resumoDados?.abandoned ?? (pagina !== null && ativos === 0 ? pagina.total : null);
  const restantes = pagina === null ? 0 : Math.max(0, pagina.total - pagina.items.length);
  const semNadaNoPeriodo = pagina !== null && pagina.total === 0 && ativos === 0;

  return (
    <section aria-labelledby="titulo-da-pagina" className="flex flex-col gap-6">
      <HeroBanner
        icon="leads"
        title="Leads"
        badges={
          <>
            {totalNoPeriodo === null ? null : <Badge tone="neutral">{formatCount(totalNoPeriodo)} no período</Badge>}
            <Badge tone="neutral" icon="escudo">
              Só depois do abandono ou vencimento
            </Badge>
          </>
        }
        description={
          <>
            Quem abandonou o checkout (30 minutos sem pagar) ou teve o plano vencido em {describeSelection(selection).toLowerCase()}, onde cada um está na régua e o que fazer a respeito. Clique num lead para ver a linha do tempo completa.
          </>
        }
        aside={<RefreshingDot active={(state.status === 'success' && state.refreshing) || (resumo.state.status === 'success' && resumo.state.refreshing)} />}
      />

      {simulado ? <SimulatedNotice>Estes leads são fictícios e servem para conferir o layout sem o motor no ar. Volume, produtos e comissão mudam com a empresa em foco.</SimulatedNotice> : null}

      <ResumoDoPeriodo estado={resumo.state} onRetry={resumo.reload} />

      {resumo.state.status === 'error' ? null : (
        <OQueFazerAgoraLeads insights={insights} carregando={resumo.state.status === 'loading'} parciais={pagina?.facets.parciais ?? false} onAcao={executarAcao} />
      )}

      <div id="tabela-de-leads" data-slot="tabela-de-leads" className="flex scroll-mt-24 flex-col gap-4">
        {state.status === 'loading' ? <ListaCarregando /> : null}

        {state.status === 'error' ? <ErrorPanel title="Não foi possível carregar os leads" error={state.error} onRetry={reload} /> : null}

        {pagina !== null ? (
          semNadaNoPeriodo ? (
            <EmptyState
              title="Nenhum lead no período"
              description="Um lead aparece aqui 30 minutos depois de um checkout ficar sem pagamento, ou quando um plano vence sem renovação. Nada acontece antes disso: é o fato que abre a régua. Experimente alargar o período no filtro acima."
            />
          ) : (
            <>
              <FiltrosDeLeads
                consulta={{ ...consulta, q: busca }}
                facets={pagina.facets}
                funis={company.funnels}
                limiarAltoValorCents={pagina.highValueThresholdCents}
                onChange={onFiltro}
                onLimpar={limparFiltros}
                onExportar={exportarCsv}
                exportando={csvGerado}
                exportavel={visiveis.length > 0}
              />

              {visiveis.length === 0 ? (
                <EmptyState
                  title="Nenhum lead corresponde aos filtros"
                  description="Os filtros combinam entre si (situação E oferta E canal). Tire um deles ou limpe tudo para ver todo mundo do período."
                  action={
                    <Button variant="secondary" icon="x" onClick={limparFiltros}>
                      Limpar filtros
                    </Button>
                  }
                />
              ) : (
                <>
                  <p className="flex flex-wrap items-center gap-x-2 text-sm text-ink-subtle">
                    <span>
                      Mostrando <strong className="tabular-nums text-ink">{formatCount(visiveis.length)}</strong> de{' '}
                      <strong className="tabular-nums text-ink">{formatCount(pagina.total)}</strong> {pagina.total === 1 ? 'lead' : 'leads'}
                      {ativos > 0 ? ' com os filtros atuais' : ' no período'}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span>
                      <span className="tabular-nums">{formatBRLFromCents(somaVisivelCents)}</span> nas linhas visíveis
                    </span>
                    {pagina.facets.parciais ? (
                      <>
                        <span aria-hidden="true">·</span>
                        <span>contagens dos chips só desta página</span>
                      </>
                    ) : null}
                  </p>

                  <LeadsTable leads={visiveis} agora={agora} sort={consulta.sort} dir={consulta.dir} onSort={ordenarPor} />

                  <div className="flex flex-wrap items-center justify-between gap-3">
                    {restantes > 0 ? (
                      <Button variant="secondary" size="lg" icon="chevron" loading={state.status === 'success' && state.refreshing} onClick={() => setLimite((l) => l + PAGE_SIZE)}>
                        Ver mais {formatCount(Math.min(PAGE_SIZE, restantes))} de {formatCount(restantes)} restantes
                      </Button>
                    ) : (
                      <p className="inline-flex items-center gap-2 text-sm text-ink-subtle">
                        <Icon name="check" size="xs" />
                        Fim da lista
                      </p>
                    )}
                    {pagina.items.length > PAGE_SIZE ? (
                      <Button variant="ghost" size="sm" onClick={() => setLimite(PAGE_SIZE)}>
                        Mostrar só os primeiros {formatCount(PAGE_SIZE)}
                      </Button>
                    ) : null}
                  </div>
                </>
              )}
            </>
          )
        ) : null}
      </div>

      <Footnote>
        Saiu em = abandonou o checkout (30 min sem pagar) ou o plano venceu. A régua só começa depois disso; a comissão existe porque recuperamos quem já tinha saído.
        {resumoDados === null
          ? ''
          : ` Período de ${formatDate(resumoDados.periodStart)} a ${formatDate(resumoDados.periodEnd)} (${describeSelection(selection)}) · ${company.name}${
              resumoDados.previous === null ? ' · sem período anterior para comparar' : ' · comparado com a janela imediatamente anterior, de mesmo tamanho'
            }`}
      </Footnote>
    </section>
  );
}

function ListaCarregando(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <p role="status" className="sr-only">
        Carregando a lista de leads
      </p>
      <div aria-hidden="true" className="h-40 animate-pulse rounded-2xl border border-line bg-surface" />
      {[0, 1, 2, 3, 4].map((linha) => (
        <div key={linha} aria-hidden="true" className="h-16 animate-pulse rounded-xl border border-line bg-surface" />
      ))}
    </div>
  );
}
